import type { UserProfile } from "@elaso/shared/schemas/profile";
import type {
	AssetAccessLevel,
	ElasoRoleRef,
} from "@elaso/shared/types/authorization";
import type { BroadcastState } from "@elaso/shared/types/broadcast";
import type {
	AttendanceSession,
	Category,
	Channel,
	CompleteMessage,
	Document,
	UserPresence,
	Workspace,
} from "@elaso/shared/types/db";
import type { OperationToolKey } from "@elaso/shared/types/operation-tools";
import type { WorkspaceCapUsage } from "@elaso/shared/types/operations";
import type { WorkspaceRevocationReason } from "@elaso/shared/types/web-socket";
import { captureException, setTag as sentrySetTag } from "@sentry/react";
import { enableMapSet } from "immer";
import { toast } from "sonner";
import { ulid } from "ulid";
import { createStore } from "zustand";
import { devtools } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { SENTRY_ENABLED, sentryLogger } from "@/lib/sentry";
import { resyncActiveAssets } from "../active-assets";
import { ws } from "../websocket-wrapper";
import { authStore } from "./auth-store";
import {
	type CachedChannel,
	dataCacheStore,
	toCachedCategory,
	toCachedChannel,
	toCachedDocument,
	toCachedWorkspace,
} from "./data-cache-store";
import type { GridItem } from "./ui-store";
import { handleUpdateStateMessage } from "./update-state-handler";

enableMapSet();

const TYPING_TIMEOUT_MS = 5000;
const TYPING_DEBOUNCE_MS = 2000;
const lastTypingSent = new Map<string, number>();
const typingTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

/**
 * Monotonic token guarding the sequential retrieval chain. Bumped at the start
 * of every loadOnConnect / selectWorkspace invocation; each awaited step bails
 * if its captured generation no longer matches, so a rapid workspace switch
 * (A→B→C) or a reconnect mid-chain abandons the stale chain instead of letting
 * a slow response clobber the current workspace's data.
 */
let loadGeneration = 0;

/**
 * Run one chain step, isolating failures. A rejection is logged but the chain
 * continues to the next step (one transient FGA timeout must not leave /app
 * half-loaded). The gate step (workspace.setCurrent) is intentionally NOT
 * wrapped, because its failure aborts the chain.
 */
async function guardStep(
	label: string,
	fn: () => Promise<void>
): Promise<void> {
	try {
		await fn();
	} catch (error) {
		sentryLogger.warn("connect chain step failed", {
			step: label,
			error: error instanceof Error ? error.message : String(error),
		});
	}
}

// Error handling for CRITICAL connect-chain steps (TECH-2958). Unlike guardStep
// (isolate + continue), critical steps are retried with backoff because /app
// can't function without them. A shorter-than-default per-attempt timeout keeps
// a HANGING step from stalling the whole sequential chain for the full 30s.
const CRITICAL_STEP_TIMEOUT_MS = 10_000;
const CRITICAL_STEP_ATTEMPTS = 3;
const CRITICAL_STEP_BACKOFF_MS = [1000, 2000];
const REFRESH_TOAST_ID = "connect-refresh";

const delay = (ms: number): Promise<void> =>
	new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Run a CRITICAL chain step with bounded retry. Surfaces the bottom-right
 * "Refreshing…" toast once it has to retry (not on a clean first attempt).
 * Returns true on success, false once attempts are exhausted. The CALLER
 * decides what exhaustion means (abort the chain, soft-degrade to read-only, or
 * show the error screen). See TECH-2958.
 *
 * `quiet` skips the retry toast. For a step whose failure the user cannot
 * perceive (nothing on screen is broken while it retries), a mid-session
 * "Refreshing data…" is noise. This chain re-runs on every reconnect, and the
 * toast loses its value for the steps where it matters. The Sentry warns
 * are kept either way.
 */
async function criticalStep(
	label: string,
	fn: () => Promise<void>,
	opts?: { quiet?: boolean }
): Promise<boolean> {
	for (let attempt = 1; attempt <= CRITICAL_STEP_ATTEMPTS; attempt++) {
		try {
			await fn();
			return true;
		} catch (error) {
			sentryLogger.warn("critical chain step failed", {
				step: label,
				attempt,
				error: error instanceof Error ? error.message : String(error),
			});
			if (attempt < CRITICAL_STEP_ATTEMPTS) {
				if (!opts?.quiet) {
					toast.loading("Refreshing data…", { id: REFRESH_TOAST_ID });
				}
				await delay(CRITICAL_STEP_BACKOFF_MS[attempt - 1] ?? 2000);
			}
		}
	}
	return false;
}

/**
 * Write-through the ACCESSIBLE channel subset for a workspace
 * into the client-side cache (D2). "Accessible" means the caller's server-resolved
 * `channel.selfPermission` is non-null, which lands WITH channel.list, so this
 * is authoritative as soon as channels are live (no roles step to wait for).
 * `toCachedChannel` is an allowlist, so no permission data (ACL or
 * `selfPermission`) is persisted; presence in the cache is the only retained
 * fact ("this was accessible at last load").
 */
/**
 * No cache write may outlive access to its workspace (TECH-3259).
 *
 * Revocation clears that workspace's cache, but writes can still be in flight
 * behind it, and they do not all travel the same road. The queued-update flush
 * is one; the lazy loaders are another, and `fetchDocuments` calls
 * `cacheDocuments` directly, off the flush path entirely, and does not run on
 * the connect chain's generation. Guarding the writers rather than each call
 * site is what covers all of them, because a future caller inherits the guard
 * instead of having to remember it.
 */
const shellWritesBlocked = () =>
	coreStore.getState().connectStatus === "revoked";

function cacheAccessibleChannels(
	workspaceId: string,
	channels: Map<string, ChannelEntry>
): void {
	if (shellWritesBlocked()) return;
	const accessible: CachedChannel[] = [];
	for (const c of channels.values()) {
		if (c.isThread) continue; // the sidebar lists top-level channels only
		if (c.selfPermission != null) accessible.push(toCachedChannel(c));
	}
	dataCacheStore.getState().setChannels(workspaceId, accessible);
}

/** Write-through cached documents, already access-scoped server-side. */
function cacheDocuments(
	workspaceId: string,
	documents: Map<string, Document>
): void {
	if (shellWritesBlocked()) return;
	const docs = Array.from(documents.values(), toCachedDocument);
	dataCacheStore.getState().setDocuments(workspaceId, docs, true);
}

/**
 * Write-through cached categories. Categories are workspace-wide
 * groupings, not access-filtered, so the full set is cached unconditionally.
 * No rolesReady gate and no accessible subset, unlike channels.
 */
function cacheCategories(
	workspaceId: string,
	categories: Map<string, Category>
): void {
	if (shellWritesBlocked()) return;
	const cats = Array.from(categories.values(), toCachedCategory);
	dataCacheStore.getState().setCategories(workspaceId, cats);
}

/** Write-through the workspace list + stamp the cache owner (D2.9). */
function cacheWorkspaces(workspaces: Map<string, Workspace>): void {
	if (shellWritesBlocked()) return;
	const cache = dataCacheStore.getState();
	cache.setOwnerUid(authStore.getState().user?.uid ?? null);
	cache.setWorkspaces(Array.from(workspaces.values(), toCachedWorkspace));
}

/**
 * Pick the workspace to load on connect: the persisted selection if it is still
 * in the fetched list, otherwise the first available, otherwise null (no
 * workspaces). Replaces the old auto-select effect in sidebar.tsx.
 */
function pickCurrentWorkspace(
	persistedId: string | null | undefined,
	workspaces: Map<string, Workspace>
): string | null {
	if (persistedId && workspaces.has(persistedId)) return persistedId;
	const first = workspaces.values().next().value;
	return first?.id ?? null;
}

/**
 * Resolve the channels visible on connect synchronously from persisted grid
 * state: the active tab of each grid item that is a text channel, deduped.
 * These are the channels whose messages the chain loads in slot 6.
 */
function visibleTextChannelIds(gridItems: GridItem[]): string[] {
	const ids = new Set<string>();
	for (const item of gridItems) {
		const activeTab = item.tabs[item.activeTabIndex];
		if (activeTab?.type === "textChannel") {
			const channelId = (activeTab.params as { channelId?: string } | undefined)
				?.channelId;
			if (channelId) ids.add(channelId);
		}
	}
	return [...ids];
}

export type ChannelPermissionEntry = {
	permissions: string[];
	direct: boolean;
};

/** An operation tool the current user may open, plus whether they may mutate it. */
export type AccessibleOpsTool = {
	toolKey: OperationToolKey;
	canMutate: boolean;
};

export type Pagination = {
	metaType: string;
	metaId: string;
	cursor: string | null;
	hasMore: boolean;
	isLoading?: boolean;
	// True once a cursor-less ("load latest") message.list fetch has completed
	// for this channel. This is the idempotency signal for the initial history
	// load. Set ONLY by a successful fetch, never by real-time message appends
	// or cache hydration, so a buffered unread message cannot masquerade as
	// "history already loaded" (TECH-3004).
	initialLoadComplete?: boolean;
};

export type UserRole = {
	id: string;
	firebaseUid: string;
	email: string;
	roles: ElasoRoleRef[];
};

export type Role = {
	id: string;
	workspaceId: string;
	name: string;
	createdAt?: string | Date;
	updatedAt?: string | Date;
};

export type UserEntry = {
	profile: UserProfile;
	roles: ElasoRoleRef[];
	presence?: UserPresence;
};

export type ChannelEntry = Omit<Channel, "permissions"> & {
	pagination?: Pagination;
	// Denormalized latest-message pointer (TECH-3020), maintained on the server.
	// Like `closedAt`, the value arrives over the wire as a JSON string but is
	// typed `Date | null` (read back via `new Date(...)`) so the entry stays
	// assignable to the Prisma `Channel` shape view components spread into.
	latestMessageId?: string | null;
	latestMessageAt?: Date | string | null;
	readStatus?: string;
	forcedUnreadMessageId?: string | null;
	mentionCount?: number;
	closedAt?: Date | string | null;
	// The full role-by-channel ACL. Shipped by `channel.listAll` only, for the
	// `/operations` permission editors. No current record owns that listAll split.
	// `/app` never receives it and must never read it
	// (docs/adr/24-what-the-channel-list-sends-to-the-client.md). Use `roleKeys` for
	// the set of roles and `selfPermission` for gating.
	permissions?: Record<string, ChannelPermissionEntry>;
	// The role keys named by this channel's ACL, and nothing else, which is what `/app`
	// gets in place of `permissions`. Drives the people popover's role grouping and
	// the `@role` mention list, neither of which reads a permission value. Kept
	// live-only like `permissions`, because it is ACL structure, so it stays out of
	// the client-side cache (an explicit allowlist, so this is the default).
	roleKeys?: string[];
	// The caller's server-resolved effective level on this channel,
	// computed server-side (caller roles ∩ ACL) and shipped with channel.list.
	// Drives visibility, posting and stage moderation, with no client-side role
	// intersection. Per-caller authz, so NEVER persisted to the client-side cache
	// (live-only, like `permissions`). Undefined on a cached channel until
	// the live channel.list lands; `null` means "no access".
	selfPermission?: AssetAccessLevel | null;
	muteStatus?: string | null;
	// Transient, never persisted. True when this channel was hydrated from the
	// client-side cache (D2) and has no live permissions yet. Grants read-only
	// visibility via use-accessible-channels until the live channel.list arrives;
	// inert once `selfPermission` is populated.
	isShell?: boolean;
};

export type { Document } from "@elaso/shared/types/db";

export type DocumentWithPermissions = Document & {
	roles: string[];
};

export type SessionEntry = {
	id: string;
	workspaceId: string;
	categoryId: string;
	name: string;
	startTime: string;
	endTime: string;
	createdBy: string;
	createdAt: string;
	updatedAt: string;
	assignmentType: "role" | "user";
	isVisible: boolean;
	startTriggered: boolean;
	endTriggered: boolean;
};

export type SessionChannelRoleAssignment = {
	sessionId: string;
	channelId: string;
	roleKey: string;
};

export type SessionChannelUserAssignment = {
	sessionId: string;
	channelId: string;
	userId: string;
};

export type TypingUser = {
	firebaseUid: string;
	channelId: string;
	timestamp: number;
};

export type CoreStoreState = {
	workspaces: Map<string, Workspace>;
	categories: Map<string, Category>;
	channels: Map<string, ChannelEntry>;
	messages: Map<string, CompleteMessage>;
	users: Map<string, UserEntry>;
	roles: Map<string, Role>;
	broadcast: BroadcastState | null;
	documents: Map<string, Document>;
	documentsWithPermissions: Map<string, DocumentWithPermissions>;
	attendanceSessions: Map<string, AttendanceSession>;
	sessions: Map<string, SessionEntry>;
	sessionChannelRoles: SessionChannelRoleAssignment[];
	sessionChannelUsers: SessionChannelUserAssignment[];
	/** Operation tools the caller may open in the current workspace. */
	accessibleOpsTools: AccessibleOpsTool[];
	/**
	 * Workspace ids the caller can use `/operations` in (TECH-3284), from
	 * `operations.listWorkspaces`. An ID SET, deliberately, not a filtered copy
	 * of `workspaces`, which stays the full Participation map that `/app` reads
	 * and that `workspace-announce` patches a row at a time with `mode:
	 * "append"`. The ops picker and its auto-select intersect the two at read,
	 * so an announced row can never re-enter the portal's list behind the
	 * filter.
	 *
	 * Connection-scoped, not workspace-scoped. Fetched once per socket open
	 * alongside the workspace list, and NOT cleared by `clearWorkspaceData`,
	 * which runs on every workspace switch. `null` means "not resolved on this
	 * connection yet" and is distinct from `[]`, which means the caller is
	 * operations-capable nowhere.
	 */
	opsWorkspaceIds: string[] | null;
	/**
	 * Cap-approach occupancy for the current workspace (TECH-3113), or `null`
	 * until loaded, or when the caller lacks the manage rung the read
	 * requires. Drives the Program-dashboard capacity gauges + warning card.
	 */
	capUsage: WorkspaceCapUsage | null;
	tourPreferences: {
		hasCompletedAppTour: boolean;
		hasCompletedMessageTour: boolean;
		hasCompletedVoiceTour: boolean;
		hasCompletedBroadcastTour: boolean;
		hasSeenFirstLoginChannels: boolean;
	} | null;
	currentActivityStatus: "active" | "idle";
	lastActivityTime: number;
	// TODO: remove this
	replyingTo: {
		id: string;
		authorId: string;
		richContent: Record<string, unknown>;
	} | null;
	stageStates: Map<
		string,
		{
			speakers: string[];
			requests: { odId: string; requestedAt: string }[];
		}
	>;
	typingUsers: Map<string, TypingUser>;
	// Mute statuses for categories (channelId mutes are on ChannelEntry)
	categoryMuteStatuses: Map<string, string | null>;
	// True while the sequential cold-connect / workspace-switch chain is in
	// flight. channel-view defers its own on-mount message fetch while set, so
	// the chain's slot 6 owns the visible channel's messages (see loadWorkspaceData).
	isColdLoading: boolean;
	// True while a lazy document.list fetch is in flight (Documents sidecar).
	// Guards against stacked refetches on rapid open/close and drives the
	// sidecar's loading-vs-empty rendering (see fetchDocuments).
	documentsLoading: boolean;
	// Whether the persistent Documents sidecar (desktop expanded panel / mobile
	// sheet) is currently open. Tracked in the store so loadWorkspaceData can
	// fold document.list into the sequential chain on a workspace switch. See
	// the note in loadWorkspaceData on why a component-fired fetch is unsafe.
	documentsSidecarOpen: boolean;
	// True once a COMPLETE document set exists for the current workspace. Set on
	// a successful document.list or on boot-hydration of a complete cached set;
	// cleared in clearWorkspaceData. Gates (a) document cache write-through to
	// complete sets only, and (b) the sidecar's render (so a partial set left by
	// an incremental create/delete broadcast never shows before the first full
	// document.list). See D2.3 / TECH-2944.
	documentsLoaded: boolean;
	// True once the caller's roles (users.roles) have loaded for the current
	// workspace, so channel-access filtering is authoritative. Until then, cached
	// channels stay visible via the isShell bypass (see
	// isChannelVisible), preventing a skeleton flash on cached refresh.
	// Set on users.roles success; reset in clearWorkspaceData.
	rolesReady: boolean;
	// True while the lazy full-matrix (users.roles) fetch is in flight. Guards
	// against stacked refetches when the channel-people popover / broadcast-speaker
	// manager open in quick succession (see fetchAllUserRoles).
	allRolesLoading: boolean;
	// True once the full all-users role matrix has loaded for the current
	// workspace. The connect chain loads only the CALLER's roles (users.myRoles);
	// the workspace-wide matrix is lazy, fetched on first open of a surface that
	// needs OTHER users' roles (channel-people popover groups users by role;
	// broadcast-speaker manager shows role badges). Freshness is then maintained by
	// the per-user users.roles delta broadcast; a reconnect clears this (via
	// clearWorkspaceData) so the next open refetches. See TECH-2951.
	allRolesLoaded: boolean;
	// True once the live channel.list has landed for the current workspace. Gates
	// cache-writes and (with rolesReady) the isShell visibility bypass, so a failed
	// or partial connect can neither clobber the channel cache nor collapse the
	// cached channels to a skeleton. Reset in clearWorkspaceData. See TECH-2958.
	channelsLive: boolean;
	// Drives /app's full-screen states for cold (uncached) loads. "ready" once the
	// cached or live data is renderable; "no-workspaces" / "error" are terminal
	// screens only meaningful when there is no cache to fall back to. "revoked" is
	// terminal too, but unlike those it can arrive mid-session and REPLACES a
	// rendered workspace, because access to the one on screen was withdrawn
	// (TECH-3259).
	connectStatus: "loading" | "ready" | "no-workspaces" | "error" | "revoked";
	// Why access ended, so the terminal screen explains the real cause rather
	// than the commonest one (TECH-3253). Only meaningful while `connectStatus`
	// is "revoked"; null otherwise.
	revokedReason: WorkspaceRevocationReason | null;
};

const getInitialState = (): CoreStoreState => ({
	workspaces: new Map(),
	categories: new Map(),
	channels: new Map(),
	messages: new Map(),
	users: new Map(),
	roles: new Map(),
	broadcast: null,
	documents: new Map(),
	documentsWithPermissions: new Map(),
	attendanceSessions: new Map(),
	sessions: new Map(),
	sessionChannelRoles: [],
	sessionChannelUsers: [],
	accessibleOpsTools: [],
	opsWorkspaceIds: null,
	capUsage: null,
	tourPreferences: null,
	currentActivityStatus: "active",
	lastActivityTime: Date.now(),
	replyingTo: null,
	stageStates: new Map(),
	typingUsers: new Map(),
	categoryMuteStatuses: new Map(),
	isColdLoading: false,
	documentsLoading: false,
	documentsSidecarOpen: false,
	documentsLoaded: false,
	rolesReady: false,
	allRolesLoading: false,
	allRolesLoaded: false,
	channelsLive: false,
	connectStatus: "loading",
	revokedReason: null,
});

type CoreStoreActions = {
	reset: () => void;
	/**
	 * End the session on this screen because Participation in `workspaceId` was
	 * revoked server-side (TECH-3259). Terminal, so the workspace is replaced by an
	 * explanation rather than left rendering content the user can no longer act
	 * on. Also drops that workspace's cached data, so a reload cannot repaint it
	 * before the server says no
	 * (docs/adr/21-what-happens-when-the-learner-client-first-connects.md).
	 *
	 * `reason` says which way access ended, either a participation revoke aimed at
	 * this user, or the whole workspace being switched off, so the screen can say
	 * the true thing (TECH-3253).
	 */
	revokeWorkspaceAccess: (
		workspaceId: string,
		reason: WorkspaceRevocationReason
	) => void;
	clearWorkspaceData: () => void;
	/**
	 * Cold-connect entry point. Runs the connect-level head of the sequential
	 * retrieval chain (workspace.list → preferences.getTourStatus), picks the
	 * current workspace, then delegates to selectWorkspace. Fired from /app on
	 * connect and re-fired on reconnect; supersession is handled by the
	 * generation token.
	 */
	loadOnConnect: () => Promise<void>;
	/** Set the current workspace in ui-store and load its data (steps 3 to 8 plus roles). */
	selectWorkspace: (workspaceId: string) => Promise<void>;
	/** Workspace-scoped tail of the chain, the strictly sequential one-in-flight load. */
	loadWorkspaceData: (workspaceId: string) => Promise<void>;
	/**
	 * Paint the client-side cache (D2) into core-store for a workspace:
	 * workspaces (boot only) + that workspace's accessible channels (read-only,
	 * `isShell`) + a complete document set if cached. Owner-uid mismatch drops the
	 * cache. Called synchronously at app boot and on workspace switch.
	 */
	hydrateFromCache: (workspaceId: string | null) => void;
	setCurrentWorkspace: (
		workspaceId: string,
		timeoutMs?: number
	) => Promise<void>;
	fetchWorkspaces: (timeoutMs?: number) => Promise<void>;
	/**
	 * Load channels into the store. `/app` calls this with `full` omitted →
	 * `channel.list` (accessible-only). `/operations` passes `full: true`
	 * → `channel.listAll` (the whole workspace set + full ACL its editors need).
	 */
	fetchChannels: (timeoutMs?: number, full?: boolean) => Promise<void>;
	clearChannels: () => void;
	fetchUsers: () => Promise<void>;
	/** Connect-chain step: load only the CALLER's roles (users.myRoles). */
	fetchUserRoles: (timeoutMs?: number) => Promise<void>;
	/**
	 * Lazy loader for the full all-users role matrix (users.roles). Fetched on
	 * first open of a surface that needs OTHER users' roles; guarded so it runs
	 * once per workspace (deltas keep it fresh, reconnect clears it).
	 */
	fetchAllUserRoles: () => Promise<void>;
	fetchRoles: () => Promise<void>;
	fetchPresences: () => Promise<void>;
	fetchBroadcastState: () => Promise<void>;
	startBroadcast: (
		title: string,
		includedRoles?: string[]
	) => Promise<BroadcastState>;
	pauseBroadcast: () => Promise<BroadcastState>;
	resumeBroadcast: () => Promise<BroadcastState>;
	stopBroadcast: () => Promise<void>;
	generateBroadcastToken: (requestSpeakerPermissions?: boolean) => Promise<{
		token: string;
		canPublish: boolean;
	}>;
	inviteSpeaker: (userFirebaseUid: string) => Promise<BroadcastState>;
	removeSpeaker: (userFirebaseUid: string) => Promise<BroadcastState>;
	createChannel: (data: {
		name: string;
		description?: string;
	}) => Promise<void>;
	updateChannel: (data: {
		id: string;
		name?: string;
		description?: string;
	}) => Promise<void>;
	deleteChannel: (data: { id: string }) => Promise<void>;
	fetchMessageHistory: (opts: {
		channelId: string;
		limit?: number;
		cursor?: string;
	}) => Promise<void>;
	sendMessage: (data: {
		channelId: string;
		/**
		 * Local-only: renders the optimistic message under the sender's name. The
		 * server derives the stored author from the authenticated connection, so
		 * this is NOT sent on the wire.
		 */
		authorId: string;
		richContent: Record<string, unknown>;
		referencedMessageId?: string;
		replyInThread?: boolean;
		threadName?: string;
		attachments?: Array<{
			fileName: string;
			fileSize: number;
			fileType: string;
			objectKey: string;
			url: string;
		}>;
	}) => Promise<void>;
	editMessage: (data: {
		id: string;
		richContent: Record<string, unknown>;
	}) => Promise<void>;
	deleteMessage: (data: { id: string }) => Promise<void>;
	setReplyingTo: (
		message: {
			id: string;
			authorId: string;
			richContent: Record<string, unknown>;
		} | null
	) => void;
	addReaction: (data: { messageId: string; emoji: string }) => Promise<void>;
	removeReaction: (data: { messageId: string; emoji: string }) => Promise<void>;
	suppressEmbeds: (data: {
		messageId: string;
		suppress: boolean;
	}) => Promise<void>;
	updatePagination: (pagination: {
		metaType: string;
		metaId: string;
		cursor: string | null;
		hasMore: boolean;
		isLoading?: boolean;
	}) => void;
	addOptimisticMessage: (message: CompleteMessage) => void;
	confirmMessage: (requestId: string, serverMessage: CompleteMessage) => void;
	failMessage: (requestId: string) => void;
	retryMessage: (requestId: string) => Promise<void>;
	deleteOptimisticMessage: (requestId: string) => void;
	fetchSessions: () => Promise<void>;
	/**
	 * Pull the caller's operation-tool grants. Resolves `true` when the lookup
	 * SUCCEEDED and the slice now holds the server's answer, `false` when it
	 * failed and the slice is untouched. A caller that gates on the grants must
	 * tell those apart, because an empty slice after a failure is "unknown", not
	 * "none" (TECH-3239). Never rejects; the same contract as `criticalStep`.
	 */
	fetchAccessibleOpsTools: () => Promise<boolean>;
	/**
	 * Resolve which workspaces `/operations` can be used in (TECH-3284).
	 * Returns whether the read SUCCEEDED. The slice is only written on success,
	 * so the route can tell "asked and got none" from "could not ask".
	 */
	fetchOpsWorkspaceIds: () => Promise<boolean>;
	fetchCapUsage: () => Promise<void>;
	fetchAttendanceSessions: () => Promise<void>;
	createAttendanceSession: (opts: {
		name: string;
	}) => Promise<AttendanceSession>;
	startAttendanceSession: (sessionId: string) => Promise<AttendanceSession>;
	stopAttendanceSession: (sessionId: string) => Promise<AttendanceSession>;
	deleteAttendanceSession: (sessionId: string) => Promise<void>;
	markChannelRead: (
		channelId: string,
		lastSeenMessageId: string
	) => Promise<void>;
	markChannelUnread: (channelId: string, messageId: string) => Promise<void>;
	markCategoryRead: (categoryId: string) => Promise<void>;
	muteTarget: (
		targetId: string,
		targetType: "channel" | "category",
		duration: string
	) => void;
	unmuteTarget: (targetId: string, targetType: "channel" | "category") => void;
	fetchTourPreferences: () => Promise<void>;
	updateTourStatus: (data: {
		tourType: "app" | "message" | "voice" | "broadcast" | "firstLoginChannels";
		completed: boolean;
	}) => Promise<void>;
	resetAllTours: () => Promise<void>;
	resetTour: (
		tourType: "app" | "message" | "voice" | "broadcast"
	) => Promise<void>;
	setActivityStatus: (status: "active" | "idle") => void;
	updateActivityStatus: (status: "active" | "idle") => void;
	fetchDocuments: () => Promise<void>;
	/** Record whether the persistent Documents sidecar is open (drives the in-chain document.list). */
	setDocumentsSidecarOpen: (open: boolean) => void;
	fetchDocumentsWithPermissions: () => Promise<void>;
	fetchStageState: (channelId: string) => Promise<void>;
	joinStage: (channelId: string) => Promise<void>;
	leaveStage: (channelId: string) => Promise<void>;
	requestStageAccess: (channelId: string) => Promise<void>;
	cancelStageRequest: (channelId: string) => Promise<void>;
	approveStageRequest: (channelId: string, targetOdId: string) => Promise<void>;
	denyStageRequest: (channelId: string, targetOdId: string) => Promise<void>;
	removeFromStage: (channelId: string, targetOdId: string) => Promise<void>;
	addTypingUser: (channelId: string, firebaseUid: string) => void;
	removeTypingUser: (channelId: string, firebaseUid: string) => void;
	startTyping: (channelId: string) => void;
	stopTyping: (channelId: string) => void;
};

type CoreStore = CoreStoreState & CoreStoreActions;

type ProcedureCall = {
	id: string;
	procedure: string;
	data?: any;
};

// Inbound UPDATE_STATE batching (TECH-3010).
// Broadcasts (messages, users.presences, unread bookkeeping) arrive at up to
// hundreds/sec in a busy channel, for example during a chat-flood with many concurrent
// senders. Applying each frame synchronously fired one Zustand notify + full
// re-render PER frame, saturating the single main thread until the UI (including
// the message editor) wedged and a real user could no longer send.
//
// We now queue inbound UPDATE_STATE frames and flush them once per animation frame
// in a SINGLE store mutation, collapsing hundreds of renders/sec into ≤60/s. A
// per-flush overlay lets accumulating writes within one frame compound correctly
// (e.g. two `messages append` frames). SUCCESS/ERROR acks are handled earlier in
// the ws wrapper and are NOT queued, so optimistic-send confirmation stays synchronous.
const updateQueue: any[] = [];
let flushScheduled = false;

/**
 * Drop queued-but-unflushed broadcasts. Called on workspace switch / reset
 * (loadGeneration bump) so a deferred rAF flush from the previous workspace
 * cannot write stale updates into freshly hydrated state. A still-pending rAF is
 * harmless, because flushUpdateQueue no-ops on an empty queue (TECH-3010).
 */
function discardQueuedUpdates() {
	updateQueue.length = 0;
	flushScheduled = false;
}

const scheduleFlush: (cb: () => void) => void =
	typeof requestAnimationFrame !== "undefined"
		? (cb) => requestAnimationFrame(() => cb())
		: (cb) => setTimeout(cb, 16);

const onBroadcastCleared = async () => {
	const { uiStore } = await import("@/lib/stores/ui-store");
	const { activeBroadcastViewer, broadcastRoom } = uiStore.getState();
	if (activeBroadcastViewer || broadcastRoom) {
		if (broadcastRoom) {
			broadcastRoom.disconnect();
		}
		uiStore.setState({
			activeBroadcastViewer: null,
			broadcastRoom: null,
		});
	}
};

function flushUpdateQueue() {
	flushScheduled = false;
	if (updateQueue.length === 0) return;
	const batch = updateQueue.splice(0, updateQueue.length);

	const base = coreStore.getState();
	// Top-level writes are accumulated into `pending` rather than applied one-by-one,
	// then committed in a single mutation below. Reads during handling go through a
	// proxy so a later frame in the same batch sees earlier frames' writes.
	const pending: Record<string, unknown> = Object.create(null);
	// `updatePagination` mutates an entry inside the `channels` map, but a paginated
	// frame also rebuilds `pending.channels` from the pre-flush snapshot, so running
	// it immediately would be clobbered by `_applyBatch`. Queue these and replay them
	// AFTER the batch commits, on the new maps (TECH-3010).
	const pendingPagination: unknown[] = [];
	// Proxy target is an EMPTY object, not `base`. immer freezes the store state,
	// so keys like `users`/`messages` are non-writable + non-configurable; a Proxy
	// over `base` must then return the target's exact value for those props, which
	// the pending overlay violates → "TypeError: 'get' on proxy: property is
	// read-only and non-configurable" (TECH-3012/3016/3017). An empty target has no
	// such props, so the overlay is legal; non-pending reads fall through to `base`.
	const workingState = new Proxy({} as any, {
		get(_target, prop) {
			if (prop === "updatePagination") {
				return (p: unknown) => pendingPagination.push(p);
			}
			if (typeof prop === "string" && prop in pending) return pending[prop];
			return (base as any)[prop];
		},
	});
	const batchedSetState = (key: string, value: unknown) => {
		pending[key] = value;
	};

	const touchedKeys = new Set<string>();
	let documentsCacheEligible = false;
	// A live workspace change (TECH-3253). A rename and a switch-on arrive as an
	// `append`, a switch-off as a `remove`. Tracked separately because the two
	// need different cache writes, see below.
	let workspacesCacheEligible = false;
	const removedWorkspaceIds: string[] = [];
	for (const message of batch) {
		// Isolate per frame. A thrower must not drop the rest of the batch or the
		// already-accumulated `pending` writes committed below (TECH-3010).
		try {
			handleUpdateStateMessage(
				message,
				workingState,
				batchedSetState,
				{
					addTypingUser: base.addTypingUser,
				},
				{ onBroadcastCleared }
			);
			touchedKeys.add(message.key);
			if (
				message.key === "documents" &&
				(message.mode === "append" || message.mode === "remove")
			) {
				documentsCacheEligible = true;
			}
			if (message.key === "workspaces") {
				if (message.mode === "append") {
					workspacesCacheEligible = true;
				}
				if (message.mode === "remove" && message.value?.id) {
					removedWorkspaceIds.push(message.value.id);
				}
			}
		} catch (error) {
			sentryLogger.error("Failed to apply UPDATE_STATE frame", {
				key: message?.key,
				mode: message?.mode,
				error: error instanceof Error ? error.message : String(error),
			});
		}
	}

	// Commit ALL accumulated top-level writes in one mutation → one notify → one render.
	if (Object.keys(pending).length > 0) {
		(coreStore.getState() as any)._applyBatch(pending);
	}

	// Replay pagination AFTER the maps are committed so it lands on the new
	// `channels` entries instead of being overwritten by `_applyBatch` (TECH-3010).
	for (const p of pendingPagination) {
		// Isolate per replay so one thrower does not drop the rest (mirrors the
		// frame loop above) (TECH-3010).
		try {
			coreStore.getState().updatePagination(p as never);
		} catch (error) {
			sentryLogger.error("Failed to replay pagination update", {
				error: error instanceof Error ? error.message : String(error),
			});
		}
	}

	// D2: keep the cached channels in sync with mid-session changes so a refresh
	// paints the up-to-date accessible set. Re-snapshot on ANY top-level `channels`
	// update. Categories are workspace-wide (no rolesReady gate). Run once on the
	// post-flush snapshot, equivalent to the previous per-message caching because it
	// caches the final state. Nested unread keys ("channels.readStatus") do not match
	// `"channels"`, so frequent unread churn does not trigger a write.
	const next = coreStore.getState();

	// D2: keep the cached workspace list in step with a live change. It is
	// otherwise written only by the connect chain, so without this a cold boot
	// repaints the old name after a rename, and a workspace that has been
	// switched off survives in the cache, where it can be hydrated and
	// auto-selected on a boot whose `workspace.list` is slow or fails
	// (TECH-3253).
	for (const removedId of removedWorkspaceIds) {
		// Drops the cached row AND that workspace's channel, category and document
		// buckets, because access to it is gone, not merely hidden. Runs even when the
		// live map is now empty, which the re-snapshot below deliberately will not.
		dataCacheStore.getState().clearWorkspace(removedId);
	}
	// Never clobber the cache with an empty list, for the same reason the connect
	// chain does not. An empty live map is far more likely to be a load that has
	// not landed than a user with no workspaces.
	if (workspacesCacheEligible && next.workspaces.size > 0) {
		cacheWorkspaces(next.workspaces);
	}

	// Derive the workspace id with a fall-back to the pre-flush snapshot. A batch
	// that deletes the last channel/document leaves the post-flush collection
	// empty, and we still need the workspace id to write the (now-empty) cache so
	// D2 does not keep painting the deleted channels (TECH-3010).
	const workspaceId =
		next.channels.values().next().value?.workspaceId ??
		next.categories.values().next().value?.workspaceId ??
		next.documents.values().next().value?.workspaceId ??
		base.channels.values().next().value?.workspaceId ??
		base.categories.values().next().value?.workspaceId ??
		base.documents.values().next().value?.workspaceId;
	if (workspaceId) {
		if (touchedKeys.has("channels") && next.channelsLive) {
			cacheAccessibleChannels(workspaceId, next.channels);
		}
		if (touchedKeys.has("categories")) {
			cacheCategories(workspaceId, next.categories);
		}
		if (documentsCacheEligible && next.documentsLoaded) {
			cacheDocuments(workspaceId, next.documents);
		}
	}
}

ws.addEventListener("message", (event) => {
	const message = JSON.parse((event as MessageEvent).data as string);

	// The server withdrew this session's access to the workspace on screen
	// (TECH-3259). Unicast to the removed user's own connections and sent just
	// before the server detaches them, so it is the only notice they get.
	// Without it the screen keeps painting a workspace they no longer participate
	// in, while every action fails. Handled in this listener rather than a second
	// one so each frame is parsed once, because every message on the socket passes
	// through here.
	if (message.type === "WORKSPACE_ACCESS_REVOKED" && message.workspaceId) {
		// A frame from before the `reason` field existed, or from a producer that
		// forgot it, still has to end the session. It degrades to the commoner
		// wording rather than to no screen at all (TECH-3253).
		coreStore
			.getState()
			.revokeWorkspaceAccess(message.workspaceId, message.reason ?? "removed");
		// Leaving the call does not wait for a render. The server evicts us from
		// LiveKit regardless; going first is what stops the local tracks cleanly.
		// Dynamically imported because ui-store imports this module.
		import("@/lib/stores/ui-store")
			.then(({ uiStore }) => uiStore.getState().disconnectAllRooms())
			.catch(() => {
				// The server-side eviction is the backstop; nothing to recover here.
			});
		return;
	}

	if (message.type !== "UPDATE_STATE") return;

	updateQueue.push(message);
	if (!flushScheduled) {
		flushScheduled = true;
		scheduleFlush(flushUpdateQueue);
	}
});

// Self-heal hook (TECH-3051). A request failing with WORKSPACE_REQUIRED means the
// socket is alive but the server connection lost its workspace context (e.g. a
// reconnect or bootstrap that did not re-send workspace.setCurrent). Re-assert the
// current workspace so the transport can transparently retry the failed request.
ws.recoverWorkspace = async () => {
	const { uiStore } = await import("@/lib/stores/ui-store");
	const workspaceId = uiStore.getState().currentWorkspaceId;
	if (!workspaceId) return;
	await ws.sendWithPromise({
		id: ulid(),
		procedure: "workspace.setCurrent",
		data: { workspaceId },
		timeoutMs: CRITICAL_STEP_TIMEOUT_MS,
	});
};

export const coreStore = createStore<CoreStore>()(
	devtools(
		immer((set, get) => ({
			_setState: (key: string, value: unknown) => {
				set((state) => {
					(state as Record<string, unknown>)[key] = value;
				});
			},
			// Commit a batch of top-level writes in a single mutation (one notify, one
			// render). Used by the coalesced UPDATE_STATE flush (TECH-3010).
			_applyBatch: (writes: Record<string, unknown>) => {
				set((state) => {
					for (const key in writes) {
						(state as Record<string, unknown>)[key] = writes[key];
					}
				});
			},
			reset: () => {
				discardQueuedUpdates();
				set(getInitialState());
			},
			revokeWorkspaceAccess: (workspaceId, reason) => {
				// Strand any connect chain still in flight. `loadOnConnect` and
				// `loadWorkspaceData` run on a captured generation and bail once it
				// no longer matches; without this bump a pending chain would carry
				// on and could put `connectStatus` back to "ready" behind the
				// terminal screen, or write the revoked workspace's cache through
				// its own cache calls (which are not on the flush path guarded
				// below).
				loadGeneration += 1;
				// Queued broadcasts for a workspace we have just been thrown out of
				// must not land behind the terminal screen.
				discardQueuedUpdates();
				dataCacheStore.getState().clearWorkspace(workspaceId);
				set((state) => {
					// Drop it from the live list too. The server will not list it
					// again, and what remains is what decides the terminal screen's
					// action, the picker if there is somewhere to go, a log-out if
					// this was their only one.
					state.workspaces.delete(workspaceId);
					state.isColdLoading = false;
					state.connectStatus = "revoked";
					state.revokedReason = reason;
				});
			},
			...getInitialState(),
			loadOnConnect: async () => {
				// Revocation is terminal for this page load (TECH-3259). The socket
				// reconnects on its own and `/app` re-runs this chain on every
				// `connectionEpoch`, so without this a reconnect would start a fresh
				// chain, overwrite "revoked" with "ready"/"no-workspaces", and switch
				// the cache-write guards back off behind the terminal screen. Both of
				// that screen's actions reload or sign out, which resets the store,
				// so latching here strands nobody.
				if (get().connectStatus === "revoked") return;
				const gen = ++loadGeneration;
				// 1. workspace.list, CRITICAL. Without workspaces (and no cache) /app
				// cannot start. Survivable when the cache has workspaces (proceed via the
				// hydrated list; SWR). See TECH-2958.
				const wsOk = await criticalStep("workspace.list", () =>
					get().fetchWorkspaces(CRITICAL_STEP_TIMEOUT_MS)
				);
				if (gen !== loadGeneration) return;
				// D2: cache the workspace list + stamp the owner for next boot's paint
				// (only when we actually have workspaces, never clobber with empty).
				if (get().workspaces.size > 0) cacheWorkspaces(get().workspaces);
				// 2. preferences.getTourStatus (isolated)
				await guardStep("preferences.getTourStatus", () =>
					get().fetchTourPreferences()
				);
				if (gen !== loadGeneration) return;

				const { uiStore } = await import("@/lib/stores/ui-store");
				const workspaceId = pickCurrentWorkspace(
					uiStore.getState().currentWorkspaceId,
					get().workspaces
				);
				if (!workspaceId) {
					// No workspace to load. Distinguish "genuinely none" (list succeeded,
					// empty → access issue) from "couldn't load" (list failed, no cache).
					set((state) => {
						state.isColdLoading = false;
						state.connectStatus = wsOk ? "no-workspaces" : "error";
					});
					if (wsOk) {
						toast.dismiss(REFRESH_TOAST_ID);
						sentryLogger.info("loadOnConnect: user has no workspaces");
					} else {
						toast.error("Couldn't load — please refresh the page", {
							id: REFRESH_TOAST_ID,
							duration: Number.POSITIVE_INFINITY,
						});
					}
					return;
				}
				if (gen !== loadGeneration) return;
				await get().selectWorkspace(workspaceId);
			},
			selectWorkspace: async (workspaceId) => {
				const { uiStore } = await import("@/lib/stores/ui-store");
				uiStore.getState().setCurrentWorkspaceId(workspaceId);
				await get().loadWorkspaceData(workspaceId);
			},
			hydrateFromCache: (workspaceId) => {
				const cache = dataCacheStore.getState();
				const uid = authStore.getState().user?.uid ?? null;
				// D2.9 cross-user safety: never paint another account's cache on a
				// shared device. A stamp mismatch drops the cache (treated as a miss;
				// the live chain refetches the real data).
				if (cache.ownerUid && uid && cache.ownerUid !== uid) {
					dataCacheStore.getState().reset();
					return;
				}
				set((state) => {
					// Workspaces: boot only, because the live list is not cleared on switch, so
					// do not clobber it. `as unknown as` because the cache holds a
					// deliberately partial row (id/name); the omitted fields are unused
					// until the live workspace.list reconciles.
					if (state.workspaces.size === 0 && cache.workspaces.length > 0) {
						const wsMap = new Map<string, Workspace>();
						for (const w of cache.workspaces) {
							wsMap.set(w.id, w as unknown as Workspace);
						}
						state.workspaces = wsMap;
					}
					if (!workspaceId) return;
					const channels = cache.channelsByWorkspace[workspaceId];
					if (channels && channels.length > 0) {
						const map = new Map<string, ChannelEntry>();
						for (const c of channels) {
							// Read-only cache entry, no live permissions yet, visible via isShell
							// (use-accessible-channels). Reconciled away by the live
							// channel.list (composite "set" replaces the whole map).
							map.set(c.id, {
								...c,
								permissions: undefined,
								isShell: true,
							} as unknown as ChannelEntry);
						}
						state.channels = map;
					}
					const categories = cache.categoriesByWorkspace[workspaceId];
					if (categories && categories.length > 0) {
						// Full set (not access-filtered); drives the sidebar's category
						// headers + ordering so they paint with the channels, not after.
						const catMap = new Map<string, Category>();
						for (const c of categories) {
							catMap.set(c.id, c as unknown as Category);
						}
						state.categories = catMap;
					}
					const docSet = cache.documentsByWorkspace[workspaceId];
					if (docSet?.complete) {
						const dmap = new Map<string, Document>();
						for (const d of docSet.docs) {
							dmap.set(d.id, d as unknown as Document);
						}
						state.documents = dmap;
						state.documentsLoaded = true;
					}
				});
			},
			loadWorkspaceData: async (workspaceId) => {
				// See `loadOnConnect`: revocation is terminal for this page load.
				if (get().connectStatus === "revoked") return;
				const gen = ++loadGeneration;
				set((state) => {
					state.isColdLoading = true;
				});
				get().clearWorkspaceData();
				// D2: re-paint the target workspace's cached data immediately after the
				// clear (synchronous, same tick, no empty frame) so a switch shows the
				// new workspace's channels read-only before the live chain reconciles.
				get().hydrateFromCache(workspaceId);
				const { uiStore } = await import("@/lib/stores/ui-store");

				// Emit this chain's terminal status + toast (TECH-2958). No-op if a
				// newer chain has superseded this one. A failed CRITICAL step leaves the
				// cached channels visible read-only (via the channelsLive gating) when one
				// exists; only an uncached failure shows the full-screen error. The roles
				// step is retried (TECH-3270) but never weighed here, because
				// gating/visibility/posting come from `channel.selfPermission` (the
				// channel.list step), so `finalize` only weighs the gate + channel.list.
				const finalize = (o: {
					gateOk: boolean;
					channelsOk: boolean;
				}): void => {
					if (gen !== loadGeneration) return;
					const hasShell = get().channels.size > 0;
					set((state) => {
						state.isColdLoading = false;
						state.connectStatus =
							(o.gateOk && o.channelsOk) || hasShell ? "ready" : "error";
					});
					if (o.gateOk && o.channelsOk) {
						toast.dismiss(REFRESH_TOAST_ID);
					} else if (hasShell) {
						// The live load failed but the cached data is already painted, so
						// the user still sees their channels (read-only until live
						// permissions reconcile). Keep the cache invisible and surface only a
						// neutral, actionable "couldn't refresh" that re-runs the chain on
						// tap. Do not reveal that what is on screen is cached.
						toast.error("Couldn't refresh — tap to retry", {
							id: REFRESH_TOAST_ID,
							duration: Number.POSITIVE_INFINITY,
							action: {
								label: "Retry",
								onClick: () => {
									void get().loadWorkspaceData(workspaceId);
								},
							},
						});
					} else {
						toast.error("Couldn't load channels — please refresh the page", {
							id: REFRESH_TOAST_ID,
							duration: Number.POSITIVE_INFINITY,
						});
					}
				};

				// 3. workspace.setCurrent, CRITICAL gate. channel.list and co. server-side
				// early-return without a workspace, so if it fails the rest is pointless.
				const gateOk = await criticalStep("workspace.setCurrent", () =>
					get().setCurrentWorkspace(workspaceId, CRITICAL_STEP_TIMEOUT_MS)
				);
				if (gen !== loadGeneration) return;
				if (!gateOk) {
					finalize({ gateOk: false, channelsOk: false });
					return;
				}
				// 4. channel.list, CRITICAL.
				const channelsOk = await criticalStep("channel.list", () =>
					get().fetchChannels(CRITICAL_STEP_TIMEOUT_MS)
				);
				if (gen !== loadGeneration) return;
				// 5. broadcast.getState (isolated)
				await guardStep("broadcast.getState", () =>
					get().fetchBroadcastState()
				);
				if (gen !== loadGeneration) return;
				// 6. message.list for the visible channel(s) (isolated), resolved
				// synchronously from persisted grid state; channel-view's own mount fetch
				// is suppressed via isColdLoading.
				for (const channelId of visibleTextChannelIds(
					uiStore.getState().gridItems
				)) {
					if (gen !== loadGeneration) return;
					await guardStep("message.list", () =>
						get().fetchMessageHistory({ channelId })
					);
				}
				if (gen !== loadGeneration) return;
				// 7. users.list (isolated)
				await guardStep("users.list", () => get().fetchUsers());
				if (gen !== loadGeneration) return;
				// 8. users.presences (isolated)
				await guardStep("users.presences", () => get().fetchPresences());
				if (gen !== loadGeneration) return;
				// + caller roles (users.myRoles), retried like a critical step
				// (TECH-3270). The server fails a permission-read error LOUD
				// (docs/adr/20-a-failed-permission-read-never-means-no-permissions.md),
				// and accepting that rejection as final would leave the caller's
				// affordances stripped until the next chain run, so retry. Exhaustion
				// soft-degrades rather than aborting. Channel visibility, posting and
				// stage moderation gate on the server-resolved `channel.selfPermission`
				// that landed with channel.list above, and the roles-reading affordances
				// (the ops-entry + broadcast buttons, the personal schedule) degrade
				// gracefully. With `rolesReady` left false, the role state stays
				// ABSENT, never a fake empty set. The result is deliberately not weighed
				// by `finalize` (see its comment), and the retry is `quiet` because nothing
				// the user can perceive is broken while it runs.
				await criticalStep(
					"users.myRoles",
					() => get().fetchUserRoles(CRITICAL_STEP_TIMEOUT_MS),
					{ quiet: true }
				);
				if (gen !== loadGeneration) return;
				// + roles.list, the name→id roster the role-name affordances bridge through
				// (isolated).
				await guardStep("roles.list", () => get().fetchRoles());
				if (gen !== loadGeneration) return;
				// + operations.getAccessibleTools, the caller's operation-tool grants
				// (TECH-3239). `/app` shows two affordances that are gated on a tool's
				// `moderate` grant: the Broadcast Controls entry and, on the ops
				// surface, the attendance manage actions. This is where they learn
				// they may. Fetched here rather than derived from role names on the
				// client, so the affordance and the gate behind it read the same answer.
				// Isolated: a failure leaves the slice as-is (empty on a cold load), so
				// the buttons stay hidden rather than the load failing.
				await guardStep("operations.getAccessibleTools", async () => {
					await get().fetchAccessibleOpsTools();
				});
				if (gen !== loadGeneration) return;
				// D2: write the ACCESSIBLE channel set through once live channels are present.
				// Accessibility comes from `channel.selfPermission`, which lands
				// WITH channel.list, so this no longer waits for the roles step. A
				// failed/partial load (no channelsLive) must not clobber the good cache.
				if (get().channelsLive) {
					cacheAccessibleChannels(workspaceId, get().channels);
				}
				// + document.list, ONLY when the Documents sidecar is already open (D3),
				// folded into the chain so it lands after the workspace.setCurrent gate
				// rather than racing it.
				if (get().documentsSidecarOpen) {
					await guardStep("document.list", () => get().fetchDocuments());
				}
				// The last await in the chain, and the only step that had no
				// generation check after it. A revocation landing while
				// `document.list` was in flight bumps the generation, and the resync
				// below would otherwise re-declare the active set for a workspace we
				// have just been removed from (TECH-3259).
				if (gen !== loadGeneration) return;
				// Re-declare the active asset set
				// (docs/adr/23-the-live-lesson-delivery-tier.md). A fresh connection (this
				// chain re-runs on every reconnect, TECH-3051) starts with an empty
				// server-side active set, and any declare that raced the chain was dropped
				// by the gate before the entitlement reconcile subscribed the eager
				// topics. The declare handler awaits that reconcile, so firing after the
				// workspace gate is safe.
				resyncActiveAssets();
				finalize({ gateOk: true, channelsOk });
			},
			setCurrentWorkspace: async (workspaceId, timeoutMs) => {
				sentryLogger.info("Switching workspace", { workspaceId });
				if (SENTRY_ENABLED) {
					sentrySetTag("workspaceId", workspaceId);
				}
				await ws.sendWithPromise({
					id: ulid(),
					procedure: "workspace.setCurrent",
					data: { workspaceId },
					timeoutMs,
				});
			},
			fetchWorkspaces: async (timeoutMs) => {
				await ws.sendWithPromise({
					id: ulid(),
					procedure: "workspace.list",
					data: {},
					timeoutMs,
				});
				// TECH-2958/3010, same hazard as `fetchChannels` below. The list has
				// landed, but it arrives as a QUEUED UPDATE_STATE frame flushed on an
				// animation frame, while this ack is delivered synchronously. Without
				// draining the queue here, `loadOnConnect`'s `pickCurrentWorkspace` can
				// read an empty `workspaces` map and declare `no-workspaces`, a
				// terminal screen, for a user who has them. The window is a few
				// milliseconds and no rAF is guaranteed inside it; an LTI launch,
				// which reconnects and mounts /app in the same breath, loses that race
				// often enough to be reported as "always". /operations' resolved-empty
				// gate (TECH-3214) reads `workspaces` after this await for the same
				// reason.
				flushUpdateQueue();
			},
			fetchChannels: async (timeoutMs, full) => {
				await ws.sendWithPromise({
					id: ulid(),
					procedure: full ? "channel.listAll" : "channel.list",
					data: {},
					timeoutMs,
				});
				// TECH-2958/3010. The live channel.list has landed, but its channels
				// arrive as a queued UPDATE_STATE. Drain the queue NOW so `channels`
				// is populated before we flip `channelsLive` (and before the cache
				// write / isShell bypass that gate on it). Without this the flag would
				// lead its data by a frame (batching reorders ack vs broadcast).
				flushUpdateQueue();
				set((state) => {
					state.channelsLive = true;
				});
			},
			clearWorkspaceData: () => {
				// Drop pending broadcasts from the workspace being torn down so a
				// deferred rAF flush can't repopulate the just-cleared maps (TECH-3010).
				discardQueuedUpdates();
				set((state) => {
					state.categories = new Map();
					state.channels = new Map();
					state.messages = new Map();
					state.users = new Map();
					state.roles = new Map();
					state.broadcast = null;
					state.documents = new Map();
					state.documentsWithPermissions = new Map();
					// Reset the lazy-load guard so a workspace switch (which empties
					// documents) does not leave it stuck and block the next fetch.
					state.documentsLoading = false;
					// Documents are no longer a complete set for the (cleared) workspace.
					state.documentsLoaded = false;
					// Re-enter cache mode, so cached channels stay visible (isShell) until
					// the new workspace's channel.list + users.roles land and filtering is
					// authoritative.
					state.rolesReady = false;
					// The lazy full matrix is workspace-scoped; drop it so the next open
					// in the new workspace (or after a reconnect) refetches.
					state.allRolesLoaded = false;
					state.allRolesLoading = false;
					state.channelsLive = false;
					state.attendanceSessions = new Map();
					state.sessions = new Map();
					state.sessionChannelRoles = [];
					state.sessionChannelUsers = [];
					state.accessibleOpsTools = [];
					state.capUsage = null;
					state.replyingTo = null;
				});

				import("@/lib/stores/ui-store").then(({ uiStore }) => {
					const { activeBroadcastViewer, broadcastRoom } = uiStore.getState();
					if (activeBroadcastViewer || broadcastRoom) {
						if (broadcastRoom) {
							broadcastRoom.disconnect();
						}
						uiStore.setState({
							activeBroadcastViewer: null,
							broadcastRoom: null,
						});
					}
				});
			},
			clearChannels: () => {
				set((state) => {
					state.channels = new Map();
				});
			},
			fetchUsers: async () => {
				await ws.sendWithPromise({
					id: ulid(),
					procedure: "users.list",
					data: {},
				});
			},
			fetchUserRoles: async (timeoutMs) => {
				// F1: the connect chain loads only the CALLER's roles via
				// users.myRoles, a zero-FGA server-side filter of conn.roles. It pushes
				// under the same `users.roles` UPDATE_STATE key (a one-element array), so
				// the reducer, this rolesReady flip, and the accessible-channel cache are
				// unchanged. The full all-users matrix is lazy (see fetchAllUserRoles).
				await ws.sendWithPromise({
					id: ulid(),
					procedure: "users.myRoles",
					data: {},
					timeoutMs,
				});
				// D2: roles are now authoritative for channel-access filtering. Flip
				// AFTER success only, because a failed fetch keeps the cached channels on screen
				// (isChannelVisible) rather than collapsing the sidebar to empty.
				set((state) => {
					state.rolesReady = true;
				});
			},
			fetchAllUserRoles: async () => {
				// Lazy loader for the workspace-wide users.roles matrix. Fired
				// on first open of channel-people popover / broadcast-speaker manager,
				// which read OTHER users' roles. Fetch-once per workspace, so skip if
				// already loaded or in flight. Freshness is maintained by the per-user
				// users.roles delta broadcast; a reconnect resets these flags (via
				// clearWorkspaceData), so an open surface refetches a clean snapshot.
				if (get().allRolesLoaded || get().allRolesLoading) return;
				set((state) => {
					state.allRolesLoading = true;
				});
				try {
					await ws.sendWithPromise({
						id: ulid(),
						procedure: "users.roles",
						data: {},
					});
					set((state) => {
						state.allRolesLoaded = true;
					});
				} finally {
					set((state) => {
						state.allRolesLoading = false;
					});
				}
			},
			fetchRoles: async () => {
				await ws.sendWithPromise({
					id: ulid(),
					procedure: "roles.list",
					data: {},
				});
			},
			fetchPresences: async () => {
				await ws.sendWithPromise({
					id: ulid(),
					procedure: "users.presences",
				});
			},
			fetchBroadcastState: async () => {
				// Called fire-and-forget by several callers (sidebar fan-out, ops
				// route, and the broadcast panel's mount effect). The panel can mount
				// from persisted grid tabs on refresh *before* workspace.setCurrent
				// has run on the new connection, in which case broadcast.getState
				// rejects with "No workspace selected". Swallow + log so the rejection
				// is not uncaught; the workspace fan-out refetches once a workspace is set.
				try {
					const result = await ws.sendWithPromise<{
						broadcast: BroadcastState | null;
					}>({
						id: ulid(),
						procedure: "broadcast.getState",
						data: {},
					});
					set((state) => {
						state.broadcast = result.broadcast;
					});
				} catch (error) {
					sentryLogger.warn("fetchBroadcastState failed", {
						error: error instanceof Error ? error.message : String(error),
					});
				}
			},
			startBroadcast: async (title, includedRoles?: string[]) => {
				sentryLogger.info("Starting broadcast", { title });
				const result = await ws.sendWithPromise<{ broadcast: BroadcastState }>({
					id: ulid(),
					procedure: "broadcast.start",
					data: { title, includedRoles },
				});
				set((state) => {
					state.broadcast = result.broadcast;
				});
				return result.broadcast;
			},
			pauseBroadcast: async () => {
				const result = await ws.sendWithPromise<{ broadcast: BroadcastState }>({
					id: ulid(),
					procedure: "broadcast.pause",
					data: {},
				});
				set((state) => {
					state.broadcast = result.broadcast;
				});
				return result.broadcast;
			},
			resumeBroadcast: async () => {
				const result = await ws.sendWithPromise<{ broadcast: BroadcastState }>({
					id: ulid(),
					procedure: "broadcast.resume",
					data: {},
				});
				set((state) => {
					state.broadcast = result.broadcast;
				});
				return result.broadcast;
			},
			stopBroadcast: async () => {
				sentryLogger.info("Stopping broadcast");
				await ws.sendWithPromise({
					id: ulid(),
					procedure: "broadcast.stop",
					data: {},
				});
				set((state) => {
					state.broadcast = null;
				});
			},
			generateBroadcastToken: async (requestSpeakerPermissions = false) => {
				const result = await ws.sendWithPromise<{
					token: string;
					canPublish: boolean;
				}>({
					id: ulid(),
					procedure: "broadcast.generateToken",
					data: { requestSpeakerPermissions },
				});
				return result;
			},
			inviteSpeaker: async (userFirebaseUid) => {
				const result = await ws.sendWithPromise<{ broadcast: BroadcastState }>({
					id: ulid(),
					procedure: "broadcast.inviteSpeaker",
					data: { userFirebaseUid },
				});
				set((state) => {
					state.broadcast = result.broadcast;
				});
				return result.broadcast;
			},
			removeSpeaker: async (userFirebaseUid) => {
				const result = await ws.sendWithPromise<{ broadcast: BroadcastState }>({
					id: ulid(),
					procedure: "broadcast.removeSpeaker",
					data: { userFirebaseUid },
				});
				set((state) => {
					state.broadcast = result.broadcast;
				});
				return result.broadcast;
			},
			createChannel: async (data) => {
				await ws.send(
					JSON.stringify({
						id: ulid(),
						procedure: "channel.create",
						data,
					} satisfies ProcedureCall)
				);
			},
			updateChannel: async (data) => {
				await ws.send(
					JSON.stringify({
						id: ulid(),
						procedure: "channel.update",
						data,
					} satisfies ProcedureCall)
				);
			},
			deleteChannel: async (data) => {
				await ws.send(
					JSON.stringify({
						id: ulid(),
						procedure: "channel.delete",
						data,
					} satisfies ProcedureCall)
				);
			},
			fetchMessageHistory: async (opts) => {
				const state = get();
				const pagination = state.channels.get(opts.channelId)?.pagination;
				if (pagination?.isLoading) return;
				// Idempotent initial load: when no cursor is supplied this is the
				// "load latest" fetch. Skip it only if a prior cursor-less fetch
				// already completed for this channel (e.g. by the cold-connect
				// chain's slot 6 or a prior mount). We key this on the pagination
				// flag, NOT on message presence, because real-time message.new
				// appends seed state.messages for channels the user has not opened
				// yet (driving unread counts); keying on presence made a single
				// buffered unread message skip the history load, so opening such a
				// channel showed only the unread messages (TECH-3004). Paginated
				// loads (with a cursor) always proceed.
				if (!opts.cursor && pagination?.initialLoadComplete) return;
				state.updatePagination({
					metaType: "channels",
					metaId: opts.channelId,
					cursor: opts.cursor || null,
					hasMore: pagination?.hasMore ?? true,
					isLoading: true,
				});
				try {
					await ws.sendWithPromise({
						id: ulid(),
						procedure: "message.list",
						data: opts,
					});
					// Mark the initial history load as done so subsequent cursor-less
					// fetches (slot 6 vs. channel-view mount) no-op. Only set on the
					// cursor-less "load latest" path; paginated loads do not gate this.
					if (!opts.cursor) {
						set((draft) => {
							const channel = draft.channels.get(opts.channelId);
							if (channel?.pagination) {
								channel.pagination.initialLoadComplete = true;
							}
						});
					}
				} catch (error) {
					sentryLogger.error("Failed to fetch message history", {
						channelId: opts.channelId,
						error: error instanceof Error ? error.message : String(error),
					});
					const current = get().channels.get(opts.channelId)?.pagination;
					if (current?.isLoading) {
						get().updatePagination({
							metaType: "channels",
							metaId: opts.channelId,
							cursor: current.cursor ?? null,
							hasMore: current.hasMore ?? true,
							isLoading: false,
						});
					}
				}
			},
			sendMessage: async (data) => {
				const requestId = ulid();

				const optimisticMessage: CompleteMessage = {
					id: requestId,
					channelId: data.channelId,
					authorId: data.authorId,
					richContent: data.richContent as any,
					type: "user",
					isPending: true,
					hasFailed: false,
					createdAt: new Date(),
					referencedMessageId: data.referencedMessageId || null,
					// A message being sent now cannot be replying to something already
					// deleted; the server stamps this only when it clears a pointer.
					referencedMessageDeletedAt: null,
					referencedMessage: data.referencedMessageId
						? get().messages.get(data.referencedMessageId) || null
						: null,
					reactions: [],
					embedData: null,
					suppressEmbeds: false,
					editedAt: null,
					threadChannelId: null,
					metadata: null,
					attachments:
						data.attachments?.map((att) => ({
							id: ulid(),
							messageId: requestId,
							fileName: att.fileName,
							fileSize: att.fileSize,
							fileType: att.fileType,
							objectKey: att.objectKey,
							url: att.url,
							createdAt: new Date(),
						})) || [],
				};

				get().addOptimisticMessage(optimisticMessage);

				const payload = {
					channelId: data.channelId,
					richContent: data.richContent,
					referencedMessageId: data.referencedMessageId,
					replyInThread: data.replyInThread,
					threadName: data.threadName,
					attachments: data.attachments,
				};

				try {
					const result = await ws.sendWithPromise<CompleteMessage>({
						id: requestId,
						procedure: "message.create",
						data: payload,
					});

					console.log(
						"[Optimistic] SUCCESS received, confirming message:",
						result
					);
					get().confirmMessage(requestId, result);
				} catch (error) {
					console.error("Failed to send message:", error);
					sentryLogger.error("Message send failed", {
						channelId: data.channelId,
						requestId,
					});
					if (SENTRY_ENABLED) {
						captureException(error, { tags: { context: "message_send" } });
					}
					get().failMessage(requestId);
				}
			},
			editMessage: async (data) => {
				const previous = get().messages.get(data.id);
				set((state) => {
					const existing = state.messages.get(data.id);
					if (existing) {
						state.messages.set(data.id, {
							...existing,
							richContent: data.richContent as any,
							editedAt: new Date(),
							isPending: true,
						});
					}
				});
				try {
					await ws.sendWithPromise({
						id: ulid(),
						procedure: "message.update",
						data,
					});
					set((state) => {
						const msg = state.messages.get(data.id);
						if (msg) {
							msg.isPending = false;
						}
					});
				} catch (error) {
					if (previous) {
						set((state) => {
							state.messages.set(data.id, previous);
						});
					}
					console.error("Failed to edit message:", error);
					sentryLogger.error("Message edit failed", { messageId: data.id });
					if (SENTRY_ENABLED) {
						captureException(error, {
							tags: { context: "message_edit" },
						});
					}
				}
			},
			deleteMessage: async (data) => {
				const previous = get().messages.get(data.id);
				set((state) => {
					state.messages.delete(data.id);
				});
				try {
					await ws.sendWithPromise({
						id: ulid(),
						procedure: "message.delete",
						data,
					});
				} catch (error) {
					if (previous) {
						set((state) => {
							state.messages.set(data.id, previous);
						});
					}
					// The optimistic removal above has just been undone, which on its own
					// is indistinguishable from the delete having been reverted. Say so
					// (TECH-3169). This path used to fire for every message that had
					// been replied to, and the only signal was a console line.
					toast.error("Couldn't delete the message — it's been restored");
					console.error("Failed to delete message:", error);
					sentryLogger.error("Message delete failed", { messageId: data.id });
					if (SENTRY_ENABLED) {
						captureException(error, {
							tags: { context: "message_delete" },
						});
					}
				}
			},
			// TODO: remove this
			setReplyingTo: (message) => {
				set((state) => {
					state.replyingTo = message;
				});
			},
			addReaction: async (data) => {
				await ws.send(
					JSON.stringify({
						id: ulid(),
						procedure: "message.addReaction",
						data,
					} satisfies ProcedureCall)
				);
			},
			removeReaction: async (data) => {
				await ws.send(
					JSON.stringify({
						id: ulid(),
						procedure: "message.removeReaction",
						data,
					} satisfies ProcedureCall)
				);
			},
			suppressEmbeds: async (data) => {
				await ws.send(
					JSON.stringify({
						id: ulid(),
						procedure: "message.suppressEmbeds",
						data,
					} satisfies ProcedureCall)
				);
			},
			updatePagination: (pagination) => {
				console.log("Updating pagination:", pagination);
				set((state) => {
					const metaCollecion =
						state[pagination.metaType as keyof CoreStoreState];
					const meta = (metaCollecion as Map<string, any>)?.get?.(
						pagination.metaId
					);
					if (meta) meta.pagination = pagination;
				});
			},
			addOptimisticMessage: (message) => {
				console.log("[Optimistic] Adding optimistic message:", message.id, {
					isPending: message.isPending,
				});
				set((state) => {
					state.messages.set(message.id, message);
				});
			},
			confirmMessage: (requestId, serverMessage) => {
				console.log("[Optimistic] Confirming message:", {
					requestId,
					serverId: serverMessage.id,
				});
				set((state) => {
					const deleted = state.messages.delete(requestId);
					console.log("[Optimistic] Deleted optimistic message:", deleted);
					state.messages.set(serverMessage.id, {
						...serverMessage,
						isPending: false,
						hasFailed: false,
					});

					if (serverMessage.channelId) {
						const channel = state.channels.get(serverMessage.channelId);

						if (channel) {
							const next = { ...channel };
							if (
								!channel.latestMessageId ||
								serverMessage.id > channel.latestMessageId
							) {
								next.latestMessageId = serverMessage.id;
								next.latestMessageAt =
									typeof serverMessage.createdAt === "string"
										? new Date(serverMessage.createdAt)
										: serverMessage.createdAt;
							}
							// This device authored the message, so confirming it means
							// everything up to it is read. Advanced in the same update
							// so the channel never renders as unread for the sender's
							// own send (TECH-3283).
							if (!next.readStatus || serverMessage.id > next.readStatus) {
								next.readStatus = serverMessage.id;
							}
							state.channels.set(serverMessage.channelId, next);
						}
					}
				});
			},
			failMessage: (requestId) => {
				set((state) => {
					const message = state.messages.get(requestId);
					if (message) {
						message.isPending = false;
						message.hasFailed = true;
					}
				});
			},
			retryMessage: async (requestId) => {
				const state = get();
				const message = state.messages.get(requestId);
				if (!message) return;

				set((s) => {
					const msg = s.messages.get(requestId);
					if (msg) {
						msg.isPending = true;
						msg.hasFailed = false;
					}
				});

				try {
					await ws.sendWithPromise({
						id: requestId,
						procedure: "message.create",
						data: {
							channelId: message.channelId,
							richContent: message.richContent,
							referencedMessageId: message.referencedMessageId || undefined,
						},
					});
				} catch (error) {
					console.error("Failed to retry message:", error);
					sentryLogger.error("Message retry failed", { requestId });
					if (SENTRY_ENABLED) {
						captureException(error, { tags: { context: "message_retry" } });
					}
					state.failMessage(requestId);
				}
			},
			deleteOptimisticMessage: (requestId) => {
				set((state) => {
					state.messages.delete(requestId);
				});
			},
			fetchSessions: async () => {
				await ws.send(
					JSON.stringify({
						id: ulid(),
						procedure: "session.list",
						data: {},
					} satisfies ProcedureCall)
				);
				await ws.send(
					JSON.stringify({
						id: ulid(),
						procedure: "session.channelRoles.list",
						data: {},
					} satisfies ProcedureCall)
				);
				await ws.send(
					JSON.stringify({
						id: ulid(),
						procedure: "session.channelUsers.list",
						data: {},
					} satisfies ProcedureCall)
				);
			},
			fetchAccessibleOpsTools: async () => {
				try {
					const result = await ws.sendWithPromise<{
						tools: AccessibleOpsTool[];
					}>({
						id: ulid(),
						procedure: "operations.getAccessibleTools",
						data: {},
					});
					set((state) => {
						state.accessibleOpsTools = result.tools;
					});
					return true;
				} catch (error) {
					// The slice is deliberately left as it was, because an affordance that
					// keeps its last answer beats one that flickers off on a transient
					// read. The FAILURE is returned rather than swallowed, because
					// `/operations` gates route access on this set and cannot read an
					// empty slice as "no grants" without ejecting an authorised admin.
					sentryLogger.warn("fetchAccessibleOpsTools failed", {
						error: error instanceof Error ? error.message : String(error),
					});
					return false;
				}
			},
			fetchOpsWorkspaceIds: async () => {
				try {
					const result = await ws.sendWithPromise<{ workspaceIds: string[] }>({
						id: ulid(),
						procedure: "operations.listWorkspaces",
						data: {},
					});
					set((state) => {
						state.opsWorkspaceIds = result.workspaceIds;
					});
					return true;
				} catch (error) {
					// Left as it was on failure, for the same reason
					// `fetchAccessibleOpsTools` above is. `/operations` decides which
					// workspace to open from this slice, and reading a failed call as
					// an empty list would point an admin's portal at nothing. The route
					// holds its loading state on `false` instead.
					sentryLogger.warn("fetchOpsWorkspaceIds failed", {
						error: error instanceof Error ? error.message : String(error),
					});
					return false;
				}
			},
			fetchCapUsage: async () => {
				// Manage rung only (TECH-3113). An ops viewer on a lower rung gets a
				// FORBIDDEN here, so leave capUsage null and the dashboard hides the
				// capacity section rather than showing an error.
				try {
					const result = await ws.sendWithPromise<WorkspaceCapUsage | null>({
						id: ulid(),
						procedure: "operations.getCapUsage",
						data: {},
					});
					set((state) => {
						state.capUsage = result;
					});
				} catch (error) {
					set((state) => {
						state.capUsage = null;
					});
					sentryLogger.warn("fetchCapUsage failed", {
						error: error instanceof Error ? error.message : String(error),
					});
				}
			},
			fetchAttendanceSessions: async () => {
				await ws.send(
					JSON.stringify({
						id: ulid(),
						procedure: "attendance.sessions.list",
						data: {},
					} satisfies ProcedureCall)
				);
			},
			createAttendanceSession: async (opts) => {
				const result = await ws.sendWithPromise<{
					session: AttendanceSession;
				}>({
					id: ulid(),
					procedure: "attendance.sessions.create",
					data: { name: opts.name },
				});
				set((state) => {
					state.attendanceSessions.set(result.session.id, result.session);
				});
				return result.session;
			},
			startAttendanceSession: async (sessionId) => {
				const result = await ws.sendWithPromise<{
					session: AttendanceSession;
				}>({
					id: ulid(),
					procedure: "attendance.sessions.start",
					data: { sessionId },
				});
				set((state) => {
					state.attendanceSessions.set(result.session.id, result.session);
				});
				return result.session;
			},
			stopAttendanceSession: async (sessionId) => {
				const result = await ws.sendWithPromise<{
					session: AttendanceSession;
				}>({
					id: ulid(),
					procedure: "attendance.sessions.stop",
					data: { sessionId },
				});
				set((state) => {
					state.attendanceSessions.set(result.session.id, result.session);
				});
				return result.session;
			},
			deleteAttendanceSession: async (sessionId) => {
				await ws.sendWithPromise({
					id: ulid(),
					procedure: "attendance.sessions.delete",
					data: { sessionId },
				});
				set((state) => {
					state.attendanceSessions.delete(sessionId);
				});
			},
			markChannelRead: async (channelId, lastSeenMessageId) => {
				// Advance the local position (and clear forced-unread, exactly
				// what the server-side markRead does) before the round-trip.
				// Waiting for the echo left the channel lit as unread for a
				// message the user was looking at (TECH-3283). The echo can only
				// confirm, because the channels.readStatus merge refuses regressions.
				set((state) => {
					const channel = state.channels.get(channelId);
					if (!channel) return;
					if (!channel.readStatus || lastSeenMessageId > channel.readStatus) {
						channel.readStatus = lastSeenMessageId;
					}
					channel.forcedUnreadMessageId = null;
				});
				await ws.send(
					JSON.stringify({
						id: ulid(),
						procedure: "channel.markRead",
						data: { channelId, lastSeenMessageId },
					} satisfies ProcedureCall)
				);
			},
			markChannelUnread: async (channelId, messageId) => {
				await ws.send(
					JSON.stringify({
						id: ulid(),
						procedure: "channel.markUnread",
						data: { channelId, messageId },
					} satisfies ProcedureCall)
				);
			},
			markCategoryRead: async (categoryId) => {
				await ws.send(
					JSON.stringify({
						id: ulid(),
						procedure: "category.markRead",
						data: { categoryId },
					} satisfies ProcedureCall)
				);
			},
			muteTarget: (targetId, targetType, duration) => {
				ws.send(
					JSON.stringify({
						id: ulid(),
						procedure: "channel.mute",
						data: { targetId, targetType, duration },
					} satisfies ProcedureCall)
				);
			},
			unmuteTarget: (targetId, targetType) => {
				ws.send(
					JSON.stringify({
						id: ulid(),
						procedure: "channel.unmute",
						data: { targetId, targetType },
					} satisfies ProcedureCall)
				);
			},
			fetchTourPreferences: async () => {
				await ws.sendWithPromise({
					id: ulid(),
					procedure: "preferences.getTourStatus",
				});
			},
			updateTourStatus: async (data) => {
				await ws.send(
					JSON.stringify({
						id: ulid(),
						procedure: "preferences.updateTourStatus",
						data,
					} satisfies ProcedureCall)
				);
			},
			resetAllTours: async () => {
				await ws.send(
					JSON.stringify({
						id: ulid(),
						procedure: "preferences.resetAllTours",
					} satisfies ProcedureCall)
				);
			},
			resetTour: async (tourType) => {
				await ws.send(
					JSON.stringify({
						id: ulid(),
						procedure: "preferences.updateTourStatus",
						data: { tourType, completed: false },
					} satisfies ProcedureCall)
				);
			},
			setActivityStatus: (status) => {
				set((state) => {
					state.currentActivityStatus = status;
					if (status === "active") {
						state.lastActivityTime = Date.now();
					}
				});
			},
			updateActivityStatus: (status) => {
				set((state) => {
					state.currentActivityStatus = status;
					if (status === "active") {
						state.lastActivityTime = Date.now();
					}
				});

				ws.send(
					JSON.stringify({
						id: ulid(),
						procedure: "users.updateActivity",
						data: { status },
					} satisfies ProcedureCall)
				);
			},
			fetchDocuments: async () => {
				// Lazy loader for the Documents sidecar (D3). Refetches on every open
				// (SWR: in-memory docs render instantly, the response reconciles by
				// replace via UPDATE_STATE mode:"set"). The in-flight guard stops rapid
				// open/close from stacking duplicate document.list requests.
				if (get().documentsLoading) return;
				set((state) => {
					state.documentsLoading = true;
				});
				try {
					await ws.sendWithPromise({
						id: ulid(),
						procedure: "document.list",
						data: {},
					});
					// TECH-3010: the documents arrive as a queued UPDATE_STATE, so drain the
					// queue NOW so `get().documents` (read by cacheDocuments below) and
					// `documentsLoaded` reflect the landed set, not the pre-flush snapshot.
					flushUpdateQueue();
					// D2: the set is now complete (document.list is server access-scoped).
					// Mark loaded and write the documents through for next boot,
					// keyed off the current workspace so an empty set is still cached as
					// "loaded, zero docs" (next boot shows "No documents", not a spinner).
					set((state) => {
						state.documentsLoaded = true;
					});
					const { uiStore } = await import("@/lib/stores/ui-store");
					const wsId = uiStore.getState().currentWorkspaceId;
					if (wsId) cacheDocuments(wsId, get().documents);
				} finally {
					set((state) => {
						state.documentsLoading = false;
					});
				}
			},
			setDocumentsSidecarOpen: (open) => {
				set((state) => {
					state.documentsSidecarOpen = open;
				});
			},
			fetchDocumentsWithPermissions: async () => {
				await ws.send(
					JSON.stringify({
						id: ulid(),
						procedure: "document.listAll",
						data: {},
					} satisfies ProcedureCall)
				);
			},
			fetchStageState: async (channelId) => {
				await ws.send(
					JSON.stringify({
						id: ulid(),
						procedure: "stage.getState",
						data: { channelId },
					} satisfies ProcedureCall)
				);
			},
			joinStage: async (channelId) => {
				await ws.send(
					JSON.stringify({
						id: ulid(),
						procedure: "stage.join",
						data: { channelId },
					} satisfies ProcedureCall)
				);
			},
			leaveStage: async (channelId) => {
				await ws.send(
					JSON.stringify({
						id: ulid(),
						procedure: "stage.leave",
						data: { channelId },
					} satisfies ProcedureCall)
				);
			},
			requestStageAccess: async (channelId) => {
				await ws.send(
					JSON.stringify({
						id: ulid(),
						procedure: "stage.request",
						data: { channelId },
					} satisfies ProcedureCall)
				);
			},
			cancelStageRequest: async (channelId) => {
				await ws.send(
					JSON.stringify({
						id: ulid(),
						procedure: "stage.cancelRequest",
						data: { channelId },
					} satisfies ProcedureCall)
				);
			},
			approveStageRequest: async (channelId, targetOdId) => {
				await ws.send(
					JSON.stringify({
						id: ulid(),
						procedure: "stage.approve",
						data: { channelId, targetOdId },
					} satisfies ProcedureCall)
				);
			},
			denyStageRequest: async (channelId, targetOdId) => {
				await ws.send(
					JSON.stringify({
						id: ulid(),
						procedure: "stage.deny",
						data: { channelId, targetOdId },
					} satisfies ProcedureCall)
				);
			},
			removeFromStage: async (channelId, targetOdId) => {
				await ws.send(
					JSON.stringify({
						id: ulid(),
						procedure: "stage.remove",
						data: { channelId, targetOdId },
					} satisfies ProcedureCall)
				);
			},
			addTypingUser: (channelId, firebaseUid) => {
				const key = `${channelId}:${firebaseUid}`;
				set((state) => {
					state.typingUsers.set(key, {
						firebaseUid,
						channelId,
						timestamp: Date.now(),
					});
				});

				const existingTimeout = typingTimeouts.get(key);
				if (existingTimeout) {
					clearTimeout(existingTimeout);
				}

				const timeout = setTimeout(() => {
					get().removeTypingUser(channelId, firebaseUid);
					typingTimeouts.delete(key);
				}, TYPING_TIMEOUT_MS);

				typingTimeouts.set(key, timeout);
			},
			removeTypingUser: (channelId, firebaseUid) => {
				const key = `${channelId}:${firebaseUid}`;
				set((state) => {
					state.typingUsers.delete(key);
				});

				const existingTimeout = typingTimeouts.get(key);
				if (existingTimeout) {
					clearTimeout(existingTimeout);
					typingTimeouts.delete(key);
				}
			},
			startTyping: (channelId) => {
				const now = Date.now();
				const lastSent = lastTypingSent.get(channelId) ?? 0;

				if (now - lastSent < TYPING_DEBOUNCE_MS) {
					return;
				}

				lastTypingSent.set(channelId, now);

				ws.send(
					JSON.stringify({
						id: ulid(),
						procedure: "typing.start",
						data: { channelId },
					})
				);
			},
			stopTyping: (channelId) => {
				// Local-only. typing.stop no longer exists server-side (the tier 1
				// fan-out fix), so receivers expire the indicator via
				// TYPING_TIMEOUT_MS. Clearing the debounce entry keeps a quick
				// restart snappy, so the next keystroke sends typing.start at once.
				lastTypingSent.delete(channelId);
			},
		})),
		{
			name: "core-store",
			serialize: {
				options: true,
			},
			enabled: true,
		} as any
	)
);
